import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Pagination } from '../components/ui/pagination';
import { Plus, Search, FileText, Calendar, Banknote, Grid3x3, List, Send, CheckCircle, Award, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { rfqAPI } from '../lib/api';
import type { RFQListItem, RFQDashboardStats } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useIsCustomer } from '../hooks/usePortalMode';
import { usePagination } from '../hooks/usePagination';
import RFQCreateWizard from '../components/rfq/RFQCreateWizard';
import { useRfqMoney } from '../components/rfq/useRfqMoney';

// The customer tabs are now the DERIVED workflow states (see
// backend/rfq/workflow_state.py); the supplier tabs stay on the stored status,
// because a supplier must never learn how many competitors quoted.
type CustomerTab = 'all' | 'draft' | 'awaiting_quotes' | 'ready_to_award' | 'no_quotes' | 'awarded' | 'cancelled';
type SupplierTab = 'all' | 'sent' | 'submitted' | 'awarded' | 'cancelled';
type StatusTab = CustomerTab | SupplierTab;

/** Tabs the customer filters on go to `workflow_state`; the supplier's go to
 *  `status`. Sending the wrong one silently returns the whole list. */
const CUSTOMER_TAB_KEYS = new Set<string>([
  'draft', 'awaiting_quotes', 'ready_to_award', 'no_quotes', 'awarded', 'cancelled',
]);

const EMPTY_STATS: RFQDashboardStats = {
  total: 0, draft: 0, sent: 0, submitted: 0, awarded: 0, cancelled: 0,
  awaiting_quotes: 0, ready_to_award: 0, no_quotes: 0,
};
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  awarded: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-300 text-gray-700',
  // Derived states. "Ready to Award" is amber because it is the only one
  // asking the buyer to do something; "Closed — No Quotes" is red because it
  // is a dead end that needs chasing or re-issuing, not a quiet resting state.
  awaiting_quotes: 'bg-blue-100 text-blue-800',
  ready_to_award: 'bg-amber-100 text-amber-800',
  no_quotes: 'bg-red-100 text-red-800',
};
const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600',
};

/** Display labels for the derived states. Mirrors WORKFLOW_STATE_LABELS in
 *  backend/rfq/workflow_state.py — keep the two in step. */
const WORKFLOW_LABELS: Record<string, string> = {
  draft: 'DRAFT',
  awaiting_quotes: 'AWAITING QUOTES',
  ready_to_award: 'READY TO AWARD',
  no_quotes: 'NO QUOTES',
  awarded: 'AWARDED',
  cancelled: 'CANCELLED',
};

type TabDef = { key: StatusTab; label: string; icon: typeof FileText | null; color: string };

// "Sent" and "Submitted" both described the wrong thing. "Sent" is the
// buyer's own act, not a state of play, and "Submitted" was a threshold flag
// meaning every invited supplier had quoted — it read as though the CUSTOMER
// submitted something, and it left the two states a buyer most needs
// unexpressible: a deadline passed with three of four quotes in, and a
// deadline passed with none at all. Both used to sit under "Sent", where an
// RFQ nobody ever answered looked identical to one issued this morning.
const CUSTOMER_TABS: TabDef[] = [
  { key: 'all', label: 'Total', icon: null, color: '' },
  { key: 'draft', label: 'Draft', icon: FileText, color: 'text-gray-600' },
  { key: 'awaiting_quotes', label: 'Awaiting Quotes', icon: Send, color: 'text-blue-600' },
  { key: 'ready_to_award', label: 'Ready to Award', icon: CheckCircle, color: 'text-amber-600' },
  { key: 'no_quotes', label: 'No Quotes', icon: AlertCircle, color: 'text-red-600' },
  { key: 'awarded', label: 'Awarded', icon: Award, color: 'text-green-600' },
  { key: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'text-gray-400' },
];

// The buying side's own words do not describe the supplier's position. "Sent"
// is the buyer's act; to the supplier it is a quote they still owe. "Draft" is
// a state they can never see at all — an unsent RFQ is not shared — so that
// tab sat there permanently reading 0.
//
// "Awarded" was the damaging one: it is the RFQ's status, not the supplier's
// outcome, so a supplier who LOST still found the job under a green "Awarded"
// heading. The row badge below now says WON or LOST instead.
const SUPPLIER_TABS: TabDef[] = [
  { key: 'all', label: 'Total', icon: null, color: '' },
  { key: 'sent', label: 'To Quote', icon: Send, color: 'text-blue-600' },
  { key: 'submitted', label: 'Quoted', icon: CheckCircle, color: 'text-yellow-600' },
  { key: 'awarded', label: 'Decided', icon: Award, color: 'text-green-600' },
  { key: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'text-gray-400' },
];

/**
 * What the status badge on a row should say, for whoever is looking.
 *
 * The customer sees the RFQ's own status. A supplier sees their position in
 * it, which is only the same thing until an award: after that the RFQ reads
 * "awarded" to everyone, and the loser was being shown a green AWARDED badge
 * on a job they did not get.
 *
 * `awarded_supplier` is already redacted per viewer by the backend
 * (`rfq/serializers/_viewer.py`): the winner gets their own id, everyone else
 * gets null and the name "Another supplier". So its presence IS the verdict —
 * no extra field and no second request needed.
 */
export function rowStatusLabel(
  // The derived fields are optional here because the function already copes
  // without them (it falls back to `status` and renders no count), and a
  // caller holding a partially-built row should not be forced to invent them.
  rfq: Pick<RFQListItem, 'status' | 'awarded_supplier'>
    & Partial<Pick<RFQListItem, 'workflow_state' | 'response_count' | 'submitted_response_count'>>,
  isCustomer: boolean,
): { label: string; tone: string } {
  if (!isCustomer && rfq.status === 'awarded') {
    return rfq.awarded_supplier !== null
      ? { label: 'WON', tone: 'bg-green-100 text-green-800' }
      : { label: 'LOST', tone: 'bg-gray-200 text-gray-700' };
  }
  if (!isCustomer && rfq.status === 'sent') {
    return { label: 'TO QUOTE', tone: STATUS_COLORS['sent'] || 'bg-gray-100' };
  }
  // The customer's badge reads the DERIVED state, and carries the count with
  // it: "3 of 4" versus "4 of 4" is what tells a buyer the field was
  // incomplete when time ran out. That distinction used to need its own
  // status; the count carries it, so one label covers one action.
  const state: string = rfq.workflow_state || rfq.status;
  const label = WORKFLOW_LABELS[state] || rfq.status.toUpperCase();
  const showCount = state === 'awaiting_quotes' || state === 'ready_to_award';
  return {
    label: showCount
      ? `${label} · ${rfq.submitted_response_count ?? 0} of ${rfq.response_count ?? 0}`
      : label,
    tone: STATUS_COLORS[state] || 'bg-gray-100',
  };
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'draft': return <FileText className="h-3 w-3" />;
    case 'sent': return <Send className="h-3 w-3" />;
    case 'submitted': return <CheckCircle className="h-3 w-3" />;
    case 'awarded': return <Award className="h-3 w-3" />;
    case 'cancelled': return <XCircle className="h-3 w-3" />;
    default: return null;
  }
}

/** Copy for the empty list: "first RFQ" only when the tenant has none at all. */
export function emptyListMessage(totalUnfiltered: number, hasSearch: boolean, statusTab: StatusTab): string {
  if (totalUnfiltered === 0 && !hasSearch) return 'No RFQs found. Create your first RFQ to get started.';
  if (hasSearch) return 'No RFQs match your search.';
  if (statusTab !== 'all') return 'No RFQs match this filter.';
  return 'No RFQs found.';
}

export default function RFQ() {
  const { formatDate } = useFormatters();
  const formatMoney = useRfqMoney();
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const statTabs = isCustomer ? CUSTOMER_TABS : SUPPLIER_TABS;
  const [rfqs, setRfqs] = useState<RFQListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [stats, setStats] = useState<RFQDashboardStats>(EMPTY_STATS);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const pagination = usePagination({ pageSize: PAGE_SIZE });
  const { currentPage, setCurrentPage, setFromResponse } = pagination;

  // Debounce typing so each keystroke doesn't hit the API. The page reset
  // happens in the same batch as the search change so the list never asks
  // for a stale page number with the new filter (DRF 404s on an out-of-range
  // page).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = searchTerm.trim();
        if (next !== prev) setCurrentPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchTerm, setCurrentPage]);

  const changeStatusTab = useCallback((tab: StatusTab) => {
    // A supplier has no Draft tab; if one is reached any other way (a stale
    // link, a restored tab), fall back to All rather than filter on a status
    // they can never hold and show an empty list with no tab highlighted.
    const allowed = (isCustomer ? CUSTOMER_TABS : SUPPLIER_TABS).some(t => t.key === tab);
    setStatusTab(allowed ? tab : 'all');
    setCurrentPage(1);
  }, [setCurrentPage, isCustomer]);

  const fetchRFQs = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page: currentPage, page_size: PAGE_SIZE };
      if (statusTab !== 'all') {
        // Customer tabs are derived states and must go to `workflow_state`;
        // `status` would not match them and the backend would return the whole
        // list rather than erroring.
        params[CUSTOMER_TAB_KEYS.has(statusTab) && isCustomer ? 'workflow_state' : 'status'] = statusTab;
      }
      if (debouncedSearch) params['search'] = debouncedSearch;
      const response = await rfqAPI.getRFQs(params);
      const data = response.data;
      if (Array.isArray(data)) {
        setRfqs(data);
        setFromResponse(data.length);
      } else {
        setRfqs(data.results ?? []);
        setFromResponse(data.count ?? 0);
      }
    } catch (error) {
      console.error('Error fetching RFQs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusTab, debouncedSearch, setFromResponse, isCustomer]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await rfqAPI.getDashboardStats();
      setStats({ ...EMPTY_STATS, ...response.data });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => { fetchRFQs(); }, [fetchRFQs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleCreate = () => {
    setShowCreateModal(false);
    fetchRFQs();
    fetchStats();
  };

  // CR-5468 rework: the wizard auto-saves progress server-side, but nothing
  // outside the wizard said so. QA filled in the first page, saw "Draft
  // saved", left, and found no trace of it anywhere — the only way back was
  // to press Create RFQ again and notice the resume prompt. Surfacing it here
  // gives the saved progress a home on the page people actually look at.
  const [wizardDraftAt, setWizardDraftAt] = useState<string | null>(null);
  useEffect(() => {
    if (!isCustomer) return;
    let cancelled = false;
    rfqAPI.getDraft()
      .then((res) => {
        if (cancelled) return;
        // 204 = no draft; the endpoint returns no body in that case.
        const data = res.data as unknown as { updated_at?: string } | null;
        setWizardDraftAt(res.status === 204 || !data ? null : data.updated_at ?? null);
      })
      .catch(() => { /* non-fatal — the banner simply doesn't show */ });
    return () => { cancelled = true; };
  }, [isCustomer, showCreateModal]);

  if (showCreateModal) {
    return <RFQCreateWizard onClose={() => setShowCreateModal(false)} onCreate={handleCreate} />;
  }

  const statValue = (key: StatusTab) => (key === 'all' ? stats.total : stats[key]) || 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">RFQ Management</h1>
          <p className="text-muted-foreground">
            {isCustomer ? 'Manage Requests for Quotation and track supplier responses' : 'Requests for Quotation you have been invited to respond to'}
          </p>
        </div>
        {isCustomer && (
          <Button onClick={() => setShowCreateModal(true)} data-tour="create-rfq-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create RFQ
          </Button>
        )}
      </div>

      {isCustomer && wizardDraftAt && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-primary" />
              <span>
                You have unfinished RFQ progress from{' '}
                {formatDate(wizardDraftAt, { includeTime: true })} — it has not been created as an RFQ yet.
              </span>
            </div>
            <Button size="sm" onClick={() => setShowCreateModal(true)}>Resume</Button>
          </CardContent>
        </Card>
      )}

      <div className={`grid grid-cols-2 gap-4 ${isCustomer ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
        {statTabs.map(({ key, label, icon: Icon, color }) => (
          <Card key={key} className={statusTab === key ? 'border-primary' : ''}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => changeStatusTab(key)}>
              <CardDescription className="flex items-center gap-1">
                {Icon && <Icon className="h-3 w-3" />} {label}
              </CardDescription>
              <CardTitle className={`text-2xl ${color}`}>{statValue(key)}</CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <Tabs value={statusTab} onValueChange={(value) => changeStatusTab(value as StatusTab)}>
        <TabsList>
          <TabsTrigger value="all">All RFQs</TabsTrigger>
          {statTabs
            .filter(({ key }) => key !== 'all')
            .map(({ key, label }) => (
              <TabsTrigger key={key} value={key}>{label}</TabsTrigger>
            ))}
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by RFQ number or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="flex gap-1">
          <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('list')} aria-label="List view">
            <List className="h-4 w-4" />
          </Button>
          <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('grid')} aria-label="Grid view">
            <Grid3x3 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div data-tour="rfq-table" className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
        {loading ? (
          <div className="col-span-full text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">Loading RFQs...</p>
          </div>
        ) : rfqs.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground">{emptyListMessage(stats.total, Boolean(debouncedSearch), statusTab)}</p>
            </CardContent>
          </Card>
        ) : (
          rfqs.map((rfq) => (
            <Card key={rfq.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/rfq/${rfq.id}`)}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{rfq.rfq_number}</CardTitle>
                      <Badge className={rowStatusLabel(rfq, isCustomer).tone}>
                        {getStatusIcon(rfq.status)}
                        <span className="ml-1">{rowStatusLabel(rfq, isCustomer).label}</span>
                      </Badge>
                      <Badge className={PRIORITY_COLORS[rfq.priority] || 'bg-gray-100'}>{rfq.priority.toUpperCase()}</Badge>
                    </div>
                    <CardDescription className="font-medium text-base text-foreground">{rfq.title}</CardDescription>
                  </div>
                  {viewMode === 'list' && (
                    <div className="text-right space-y-1 ml-4">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Due: {formatDate(rfq.response_deadline)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm font-semibold">
                        <Banknote className="h-4 w-4" />
                        <span>{formatMoney(rfq.total_estimated_value, rfq.currency)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
                  <span>Category: <span className="font-medium text-foreground capitalize">{rfq.category}</span></span>
                  {viewMode === 'grid' && (
                    <span>Value: <span className="font-medium text-foreground">{formatMoney(rfq.total_estimated_value, rfq.currency)}</span></span>
                  )}
                  <span className="ml-auto">Created: {formatDate(rfq.created_at)}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {!loading && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          itemsPerPage={pagination.pageSize}
          onPageChange={pagination.goToPage}
          itemLabel="RFQs"
        />
      )}
    </div>
  );
}
